/**
 * AdminUsersPage — user list for the new admin surface (/admin/home/users).
 *
 * Reads the existing GET /api/admin/users endpoint via adminService. The
 * legacy console's own user tab (inside pages/AdminDashboard.js at
 * /admin/users) is untouched.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Avatar,
  Chip,
  Skeleton,
  Alert,
  Button,
  Link,
  TextField,
  MenuItem,
  InputAdornment,
  IconButton,
  Stack,
} from '@mui/material';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';

import adminService from '../services/adminService';
import RoleChip from '../components/admin/RoleChip';
import AssignmentCount from '../components/admin/AssignmentCount';
import { panelSx, tableSx, tableRowSx } from '../components/admin/surfaces';
import { brand, status as statusTokens, text, bg, border } from '../theme/colors';

const ALL_COLUMNS = ['Image', 'Name', 'Email', 'Role', 'Status', 'Joined'];
// Clients own projects, so the Clients list carries a count column. Other
// roles have no owned projects, so it only appears there.
const CLIENT_COLUMNS = ['Image', 'Name', 'Email', 'Status', 'Projects', 'Joined'];

const PAGE_SIZES = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

// users_role_check / users_status_check.
const ROLE_OPTIONS = [
  'client', 'contractor', 'verifier', 'quality_inspector',
  'project_manager', 'merchant', 'moderator', 'admin',
];
const STATUS_OPTIONS = ['pending', 'active', 'suspended', 'deactivated', 'banned'];

const SEARCH_DEBOUNCE_MS = 400;

// The five values users_status_check permits on the live DB.
const STATUS_TONES = {
  active: { fg: statusTokens.successDark, bg: statusTokens.successBg },
  pending: { fg: statusTokens.warningDark, bg: statusTokens.warningBg },
  suspended: { fg: statusTokens.warningDark, bg: statusTokens.warningBg },
  deactivated: { fg: statusTokens.neutral, bg: statusTokens.neutralBg },
  banned: { fg: statusTokens.errorDark, bg: statusTokens.errorBg },
};

const titleise = (value) => (value || '').replace(/_/g, ' ');

const initialsOf = (user) => {
  const first = (user.firstName || '').trim();
  const last = (user.lastName || '').trim();
  if (first || last) return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  return (user.email || '?').charAt(0).toUpperCase();
};

const fullNameOf = (user) => `${user.firstName || ''} ${user.lastName || ''}`.trim();

const formatJoined = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

/**
 * @param lockedRole  when set, the list is pinned to this role and the role
 *                    picker is hidden — used by the Clients entry.
 * @param title       heading override.
 * @param noun        singular noun for the count line.
 */
const AdminUsersPage = ({ lockedRole = null, title = 'Users', noun = 'user' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination lives in the URL, not in state, so ?page=3 is authoritative on
  // first paint and the back button works. `page` is 1-based in the URL (what
  // a human types) and 0-based internally (what TablePagination wants).
  // A single-role list doesn't need a Role column repeating the same value.
  const COLUMNS = lockedRole === 'client'
    ? CLIENT_COLUMNS
    : lockedRole
      ? ALL_COLUMNS.filter((c) => c !== 'Role')
      : ALL_COLUMNS;

  const parsedPage = parseInt(searchParams.get('page'), 10);
  const page = Number.isFinite(parsedPage) && parsedPage > 0 ? parsedPage - 1 : 0;

  const parsedLimit = parseInt(searchParams.get('limit'), 10);
  const rowsPerPage = PAGE_SIZES.includes(parsedLimit) ? parsedLimit : DEFAULT_PAGE_SIZE;

  const urlSearch = searchParams.get('search') || '';
  const roleFilter = lockedRole
    || (ROLE_OPTIONS.includes(searchParams.get('role')) ? searchParams.get('role') : 'all');
  const statusFilter = STATUS_OPTIONS.includes(searchParams.get('status'))
    ? searchParams.get('status')
    : 'all';
  const hasFilters =
    !!urlSearch || (!lockedRole && roleFilter !== 'all') || statusFilter !== 'all';

  // Empty / 'all' values are dropped so a default view has a clean URL.
  const writeParams = (updates, { replace = false } = {}) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === '' || value === 'all' || value == null) params.delete(key);
      else params.set(key, String(value));
    });
    setSearchParams(params, { replace });
  };

  // `push` for paging (back/forward step through pages); `replace` for
  // corrections, so a clamp doesn't leave a dead entry in history.
  const setPagination = (nextPage, nextLimit = rowsPerPage, { replace = false } = {}) =>
    writeParams(
      {
        page: nextPage > 0 ? nextPage + 1 : '',
        limit: nextLimit !== DEFAULT_PAGE_SIZE ? nextLimit : '',
      },
      { replace }
    );

  // Any filter change invalidates the current offset — page 3 of the old
  // result set is meaningless against the new one.
  const setFilter = (updates, { replace = false } = {}) =>
    writeParams({ ...updates, page: '' }, { replace });

  /**
   * `isCancelled` guards against out-of-order responses: a slower earlier
   * request must not overwrite a newer one, or the table renders rows for a
   * filter the user has already moved off. Supplied by the effect below;
   * user-initiated calls (Retry, refresh) pass nothing and always apply.
   */
  const load = useCallback(async ({ isCancelled = () => false } = {}) => {
    setLoading(true);
    try {
      const { users: rows, pagination } = await adminService.getUsers({
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        ...(urlSearch ? { search: urlSearch } : {}),
        ...(roleFilter !== 'all' ? { role: roleFilter } : {}),
        ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
      });
      if (isCancelled()) return;
      setUsers(rows || []);
      setTotal(pagination?.total ?? 0);
      setError(null);
    } catch (err) {
      if (isCancelled()) return;
      setError(err?.response?.data?.error || err?.message || 'Could not load users.');
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  }, [page, rowsPerPage, urlSearch, roleFilter, statusFilter]);

  useEffect(() => {
    let cancelled = false;
    load({ isCancelled: () => cancelled });
    return () => { cancelled = true; };
  }, [load]);

  // The text box is local so typing stays responsive; it lands in the URL
  // after a pause. `replace` so each keystroke doesn't become a history entry.
  const [searchInput, setSearchInput] = useState(urlSearch);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput.trim() !== urlSearch) {
        setFilter({ search: searchInput.trim() }, { replace: true });
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput, urlSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pull the box back in step when the URL changes underneath it (back button,
  // Clear all). The guard leaves in-flight typing alone.
  useEffect(() => {
    setSearchInput((current) => (current.trim() === urlSearch ? current : urlSearch));
  }, [urlSearch]);

  // ?page=999 lands on an empty offset. Once the count is known, step back to
  // the last page that actually holds rows.
  useEffect(() => {
    if (loading || total === 0 || page === 0) return;
    const lastPage = Math.max(0, Math.ceil(total / rowsPerPage) - 1);
    if (page > lastPage) setPagination(lastPage, rowsPerPage, { replace: true });
  }, [loading, total, page, rowsPerPage]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box sx={{ p: 3 }}>
      <Typography sx={{ fontSize: 22, fontWeight: 700, color: text.primary, mb: 0.25 }}>
        {title}
      </Typography>
      <Typography variant="body2" sx={{ color: text.secondary, mb: 2.5 }}>
        {loading
          ? 'Loading…'
          : `${total.toLocaleString()} ${noun}${total === 1 ? '' : 's'}${
              hasFilters ? ' matching' : ''
            }`}
      </Typography>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={1.5}
        sx={{ mb: 2.5 }}
        alignItems={{ xs: 'stretch', md: 'center' }}
      >
        <TextField
          size="small"
          placeholder="Search by name or email"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          sx={{ flex: 1, minWidth: { md: 260 }, bgcolor: bg.paper }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: text.tertiary }} />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchInput('')} aria-label="Clear search">
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        {!lockedRole && (
          <TextField
            select
            size="small"
            label="Role"
            value={roleFilter}
            onChange={(e) => setFilter({ role: e.target.value })}
            sx={{ minWidth: 170, bgcolor: bg.paper }}
          >
            <MenuItem value="all">All roles</MenuItem>
            {ROLE_OPTIONS.map((r) => (
              <MenuItem key={r} value={r} sx={{ textTransform: 'capitalize' }}>
                {r.replace(/_/g, ' ')}
              </MenuItem>
            ))}
          </TextField>
        )}

        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => setFilter({ status: e.target.value })}
          sx={{ minWidth: 160, bgcolor: bg.paper }}
        >
          <MenuItem value="all">All statuses</MenuItem>
          {STATUS_OPTIONS.map((st) => (
            <MenuItem key={st} value={st} sx={{ textTransform: 'capitalize' }}>
              {st}
            </MenuItem>
          ))}
        </TextField>

        {hasFilters && (
          <Button
            onClick={() =>
              setFilter({ search: '', status: 'all', ...(lockedRole ? {} : { role: 'all' }) })
            }
            sx={{ textTransform: 'none', color: text.secondary, whiteSpace: 'nowrap' }}
          >
            Clear all
          </Button>
        )}
      </Stack>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2.5, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={load}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {/* panelSx carries the border, radius, shadow and the overflow clip that
          keeps the header fill inside the rounded corners. */}
      <Paper elevation={0} sx={panelSx}>
        <TableContainer>
          <Table size="medium" sx={tableSx}>
            <TableHead>
              <TableRow>
                {COLUMNS.map((label) => (
                  <TableCell key={label}>{label}</TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    {COLUMNS.map((label) => (
                      <TableCell key={label}>
                        <Skeleton height={28} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={COLUMNS.length} sx={{ py: 6, textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ color: text.tertiary }}>
                      {hasFilters
                        ? `No ${noun}s match these filters.`
                        : `No ${noun}s found.`}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => {
                  const tone = STATUS_TONES[user.status] || {
                    fg: statusTokens.neutral,
                    bg: statusTokens.neutralBg,
                  };
                  const name = fullNameOf(user);
                  return (
                    <TableRow key={user.id} sx={tableRowSx}>
                      <TableCell sx={{ width: 64 }}>
                        <Avatar
                          src={user.profileImageUrl || undefined}
                          alt={name || user.email}
                          sx={{
                            width: 36,
                            height: 36,
                            fontSize: 13,
                            fontWeight: 600,
                            bgcolor: `${brand.primary}1a`,
                            color: brand.primary,
                          }}
                        >
                          {initialsOf(user)}
                        </Avatar>
                      </TableCell>

                      <TableCell>
                        <Link
                          component="button"
                          type="button"
                          underline="hover"
                          onClick={() =>
                            navigate(`/admin/home/users/${user.id}`, {
                              state: { listSearch: location.search, listPath: location.pathname },
                            })
                          }
                          sx={{
                            color: text.primary,
                            fontWeight: 500,
                            fontSize: 'inherit',
                            fontFamily: 'inherit',
                            textAlign: 'left',
                            cursor: 'pointer',
                            '&:hover': { color: brand.primary },
                          }}
                        >
                          {name || user.email}
                        </Link>
                      </TableCell>

                      <TableCell sx={{ color: text.secondary }}>{user.email}</TableCell>

                      {!lockedRole && (
                        <TableCell>
                          <RoleChip role={user.role} />
                        </TableCell>
                      )}

                      <TableCell>
                        <Chip
                          size="small"
                          label={titleise(user.status) || 'unknown'}
                          sx={{
                            textTransform: 'capitalize',
                            fontWeight: 500,
                            fontSize: 12,
                            color: tone.fg,
                            bgcolor: tone.bg,
                          }}
                        />
                      </TableCell>

                      {lockedRole === 'client' && (
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <AssignmentCount
                            stats={user.projectStats}
                            sourceLabels={{ owned: 'owned' }}
                          />
                        </TableCell>
                      )}

                      <TableCell sx={{ color: text.secondary, whiteSpace: 'nowrap' }}>
                        {formatJoined(user.createdAt)}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, next) => setPagination(next)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => setPagination(0, parseInt(e.target.value, 10))}
          rowsPerPageOptions={PAGE_SIZES}
          sx={{ borderTop: `1px solid ${border.light}` }}
        />
      </Paper>
    </Box>
  );
};

export default AdminUsersPage;
